import { Component, ElementRef, forwardRef, inject, output, signal, viewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CountryService } from '@insolution/countries-data-access';
import { CountriesResponseItem } from '@insolution/graphql-data-access';
import { NgbHighlight, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, map, merge, Observable, OperatorFunction, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ELanguage } from '@insolution/shared-data-access';

@Component({
  selector: 'lib-country-selector',
  imports: [NgbTypeahead, NgbHighlight, TranslatePipe],
  templateUrl: './country-selector.html',
  styleUrl: './country-selector.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelector),
      multi: true,
    },
  ],
})
export class CountrySelector {
  private countryInput = viewChild<ElementRef<HTMLInputElement>>('countryInput');

  public countriesFetched = output<boolean>();

  private countryService = inject(CountryService);
  private translateService = inject(TranslateService);

  private countries = signal<CountriesResponseItem[]>([]);
  private selectedCountry = signal<string | null>(null);

  protected countrySelectorFocus$ = new Subject<string>();
  protected countrySelectorClick$ = new Subject<string>();

  private onChange = (value: string | null) => {
    /* intentionally empty */
  };
  private onTouched = () => {
    /* intentionally empty */
  };
  protected disabled = false;

  constructor() {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe((lang) => this.setCountries(lang.lang as ELanguage));

    this.setCountries(this.translateService.getCurrentLang() as ELanguage);
  }

  writeValue(value: string | null): void {
    this.selectedCountry.set(value);

    if (!value) {
      return;
    }

    if (!this.countries().length) {
      return;
    }

    const foundCountry = this.countries().find((country) => country.countryCode === value);
    if (foundCountry) {
      this.setCountryInputValue(foundCountry);
      return;
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private async setCountries(language: ELanguage): Promise<void> {
    const countries = await this.countryService.getCountries(language);

    this.countries.set(countries);

    this.countriesFetched.emit(true);
  }

  protected countrySearch: OperatorFunction<string, readonly CountriesResponseItem[]> = (text$: Observable<string>) =>
    merge(
      text$.pipe(debounceTime(200), distinctUntilChanged()),
      this.countrySelectorFocus$,
      this.countrySelectorClick$
    ).pipe(
      map((term) =>
        !term.length
          ? this.countries()
          : this.countries()
              .filter(
                (v) =>
                  v.name.toLowerCase().indexOf(term.toLowerCase()) > -1 ||
                  v.countryCode.toLowerCase().indexOf(term.toLowerCase()) > -1
              )
              .slice(0, 10)
      )
    );

  protected countryInputFormatter = (country: CountriesResponseItem) => {
    if (typeof country === 'string') {
      const foundCountry = this.countries().find((c) => c.countryCode === country);
      return foundCountry ? foundCountry.name : country;
    }

    return country?.name ?? '';
  };

  protected onCountrySelect(event: any) {
    event.preventDefault();
    const selectedCountry = event.item as CountriesResponseItem;

    this.selectedCountry.set(selectedCountry.countryCode);
    this.onChange(selectedCountry.countryCode);
    this.onTouched();

    this.setCountryInputValue(selectedCountry);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  protected onCountryInput(value: string): void {
    if (value.trim()) {
      return;
    }

    this.selectedCountry.set(null);
    this.onChange(null);
  }

  private setCountryInputValue(countryCode: CountriesResponseItem): void {
    const typeaheadInstance = this.countryInput();
    if (typeaheadInstance) {
      const inputElement = typeaheadInstance.nativeElement as HTMLInputElement;
      if (inputElement) {
        inputElement.value = countryCode.name;
      }
    }
  }
}
